/**
 * templateUtils — Step 16 / 16.5 / 18.1
 *
 * Pure utilities for template interpolation and template diagnostics.
 * Shared between the Preview runtime (usePreviewRuntime) and the
 * Actions & Bindings inspector (ActionInspector).
 *
 * Supported namespaces:
 *   {{components.<name>.value}}   — component runtime value (Steps 16–17)
 *   {{variables.<name>}}          — global runtime variable  (Step 18.1)
 *
 * Interpolation rules — components:
 *   Component found + has textValue  → use textValue
 *   Component found, no textValue    → use "" (empty string)
 *   Component not found              → throw Error (caller surfaces it)
 *
 * Interpolation rules — variables:
 *   string           → original string
 *   number/boolean   → String(value)
 *   object/array     → JSON.stringify(value)
 *   null             → "null"
 *   undefined        → "" (variable declared but not yet set)
 *   undeclared       → throw Error (configuration error — caller surfaces it)
 *
 * No project JSON is ever mutated here.  All functions return new objects
 * or plain values.
 */

import type { RestAction } from '../../types/project';

// ── Template token ────────────────────────────────────────────────────────────

/**
 * A single parsed template token found in an action field.
 *
 * For component tokens:
 *   namespace  = "components"
 *   name       = component name  (e.g. "hostnameInput")
 *   property   = "value"
 *   variableName = null
 *
 * For variable tokens:
 *   namespace  = "variables"
 *   name       = variable name   (e.g. "selectedHostId")
 *   property   = null
 *   variableName = variable name (same as name, convenience accessor)
 *
 * raw is the full placeholder string, e.g. "{{components.hostnameInput.value}}".
 */
export type TemplateToken = {
  /** The full placeholder string */
  raw: string;
  /** Where in the action this token was found, e.g. "queryParameters.hostname" */
  location: string;
  /** "components" | "variables" */
  namespace: 'components' | 'variables';
  /** Component name or variable name */
  name: string;
  /** Component property (e.g. "value"); null for variable tokens */
  propertyName: string | null;
  /** True if the token matched a supported pattern */
  wellFormed: boolean;
  // ── Deprecated aliases kept for backward compatibility ──────────────────────
  /** @deprecated Use `name` instead */
  componentName: string | null;
};

// ── Malformed token ───────────────────────────────────────────────────────────

/**
 * A suspected-but-malformed template token: an opening "{{" was found but
 * the content did not close with "}}" before end-of-string, OR an expression
 * beginning with "{{variables." that does not match the valid grammar
 * {{variables.<singleName>}} (variable names may not contain dots).
 */
export type MalformedToken = {
  /** The partial text that starts with "{{" */
  raw: string;
  /** Where in the action this fragment was found */
  location: string;
};

// ── Variable expression classifier ────────────────────────────────────────────

/**
 * Classifies a raw template string segment as it relates to the variables namespace.
 *
 * Returns:
 *   'valid'    — the segment is exactly {{variables.<singleName>}} (no dots in name)
 *   'malformed' — the segment starts with {{variables. but is not valid grammar
 *   'none'     — the segment does not contain a {{variables. prefix at all
 *
 * This is the single source of truth used by both the extractor/runtime
 * (templateUtils) and the Inspector (ActionInspector) so they cannot disagree.
 *
 * Valid grammar:  {{variables.<singleName>}}
 *   where <singleName> is one or more characters that are not ".", "}", or "{".
 *
 * Malformed examples:
 *   {{variables.customer.id}}  — dot in name
 *   {{variables.}}             — empty name
 *   {{variables.name}          — unclosed (only one closing brace)
 *   {{variables.name.extra}}   — dot in name
 */
export function classifyVariableExpression(
  segment: string,
): 'valid' | 'malformed' | 'none' {
  if (!segment.includes('{{variables.')) return 'none';
  if (/\{\{variables\.([^}.]+)\}\}/.test(segment)) return 'valid';
  return 'malformed';
}

// ── Extraction ────────────────────────────────────────────────────────────────

// Matches {{components.<name>.<prop>}}
const COMPONENT_RE = /\{\{components\.([^}.]+)\.([^}]+)\}\}/g;
// Matches {{variables.<name>}} — name must not contain dots or closing braces
const VARIABLE_RE = /\{\{variables\.([^}.]+)\}\}/g;

/**
 * Extracts all well-formed component and variable tokens from a string.
 */
function extractFromString(value: string, location: string): TemplateToken[] {
  const tokens: TemplateToken[] = [];

  COMPONENT_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = COMPONENT_RE.exec(value)) !== null) {
    tokens.push({
      raw: m[0],
      location,
      namespace: 'components',
      name: m[1],
      propertyName: m[2],
      wellFormed: true,
      componentName: m[1], // backward-compat alias
    });
  }

  VARIABLE_RE.lastIndex = 0;
  while ((m = VARIABLE_RE.exec(value)) !== null) {
    tokens.push({
      raw: m[0],
      location,
      namespace: 'variables',
      name: m[1],
      propertyName: null,
      wellFormed: true,
      componentName: null, // not a component token
    });
  }

  return tokens;
}

/**
 * Detects malformed template syntax in a string value:
 *
 * Pass 1 — malformed {{variables.…}} expressions:
 *   Scans for every {{variables. candidate and classifies it.  Each malformed
 *   result's opener index is recorded so pass 2 can suppress the duplicate
 *   generic diagnostic for the same defect.
 *
 * Pass 2 — genuine unmatched openers:
 *   Scans left-to-right with a stack to find the positions of openers that are
 *   never matched by a subsequent "}}" closer.  For each such position that is
 *   NOT already covered by a malformed variable expression from pass 1, a
 *   generic missing-closing-braces entry is emitted from the exact unmatched
 *   opener position.
 *
 * Returns exactly one MalformedToken per distinct problem.
 */
function detectMalformed(value: string, location: string): MalformedToken[] {
  const results: MalformedToken[] = [];

  // ── Pass 1: malformed {{variables.…}} expressions ─────────────────────────
  // Record the opener position of each malformed variable expression so pass 2
  // can avoid emitting a duplicate generic diagnostic for the same opener.
  const varNsRe = /\{\{variables\.[^}]*\}?\}?/g;
  const malformedVarOpenerPositions = new Set<number>();
  let m: RegExpExecArray | null;
  varNsRe.lastIndex = 0;
  while ((m = varNsRe.exec(value)) !== null) {
    const candidate = m[0];
    if (classifyVariableExpression(candidate) === 'malformed') {
      results.push({ raw: candidate, location });
      malformedVarOpenerPositions.add(m.index);
    }
  }

  // ── Pass 2: genuine unmatched openers via left-to-right stack scan ─────────
  // Walk the string character by character, pushing opener positions onto a
  // stack on "{{" and popping on "}}".  Positions left on the stack at the end
  // are genuinely unmatched.  We emit a generic diagnostic only for positions
  // that are NOT already covered by a malformed variable expression (pass 1).
  const openerStack: number[] = [];
  for (let i = 0; i < value.length - 1; i++) {
    if (value[i] === '{' && value[i + 1] === '{') {
      openerStack.push(i);
      i++; // skip second '{' so we don't double-count
    } else if (value[i] === '}' && value[i + 1] === '}') {
      if (openerStack.length > 0) {
        openerStack.pop();
      }
      i++; // skip second '}'
    }
  }
  // openerStack now contains the positions of every unmatched "{{".
  for (const idx of openerStack) {
    if (!malformedVarOpenerPositions.has(idx)) {
      const fragment = value.slice(idx, Math.min(idx + 40, value.length));
      results.push({ raw: fragment, location });
    }
  }

  return results;
}

/**
 * Extracts all template tokens from all interpolatable fields of a REST action.
 * Returns { tokens, malformed }.
 */
export function extractTemplates(action: RestAction): {
  tokens: TemplateToken[];
  malformed: MalformedToken[];
} {
  const tokens: TemplateToken[] = [];
  const malformed: MalformedToken[] = [];

  function scan(value: string, location: string) {
    tokens.push(...extractFromString(value, location));
    malformed.push(...detectMalformed(value, location));
  }

  scan(action.url, 'url');

  if (action.bodyTemplate) {
    scan(action.bodyTemplate, 'bodyTemplate');
  }

  for (const [key, val] of Object.entries(action.headers ?? {})) {
    scan(val, `headers.${key}`);
  }
  for (const [key, val] of Object.entries(action.queryParameters ?? {})) {
    scan(val, `queryParameters.${key}`);
  }
  for (const [key, val] of Object.entries(action.pathParameters ?? {})) {
    scan(val, `pathParameters.${key}`);
  }

  // De-duplicate identical token.raw+location combinations
  const seen = new Set<string>();
  const dedupedTokens = tokens.filter((t) => {
    const k = `${t.location}::${t.raw}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  return { tokens: dedupedTokens, malformed };
}

// ── Runtime interpolation ─────────────────────────────────────────────────────

type ComponentRuntimeState = {
  /**
   * TextInput: current typed string value.
   * Used for {{components.<name>.value}} template interpolation.
   */
  textValue?: string;
  /**
   * Dropdown: current selected option value (string).
   * Also used for {{components.<name>.value}} template interpolation.
   * Label/JsonViewer: runtime display value (may be non-string).
   */
  value?: unknown;
  [key: string]: unknown;
};

/**
 * Converts a runtime variable value to a string for template insertion.
 *
 * Conversion rules (Step 18.1):
 *   undefined → ""
 *   null      → "null"
 *   string    → original string
 *   number    → String(value)
 *   boolean   → String(value)
 *   object    → JSON.stringify(value)
 *   array     → JSON.stringify(value)
 */
function variableValueToString(value: unknown): string {
  if (value === undefined) return '';
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  // object or array
  return JSON.stringify(value);
}

/**
 * Interpolates all {{components.<name>.value}} and {{variables.<name>}}
 * placeholders in a string.
 *
 * Throws if a referenced component name is not in componentsByName.
 * Throws if a referenced variable name is not in variableState (undeclared).
 * Declared variables with undefined runtime values resolve to "".
 *
 * Resolution order (per component):
 *   1. textValue (TextInput — Step 16 original behaviour)
 *   2. value cast to string (Dropdown — Step 17.2)
 *   3. "" (empty string) when neither is set
 */
export function interpolateString(
  template: string,
  componentsByName: Map<string, string>,
  componentState: Record<string, ComponentRuntimeState>,
  variableState: Record<string, unknown>,
  declaredVariableNames: Set<string>,
): string {
  // Replace component placeholders first
  let result = template.replace(
    /\{\{components\.([^}]+)\.value\}\}/g,
    (_match, name: string) => {
      if (!componentsByName.has(name)) {
        throw new Error(
          `Template references component "${name}" which does not exist in the project.`,
        );
      }
      const id = componentsByName.get(name)!;
      const state = componentState[id];
      if (state?.textValue !== undefined) return state.textValue;
      if (state?.value !== undefined && state.value !== null) return String(state.value);
      return '';
    },
  );

  // ── Malformed variable expressions — reject before any substitution ─────────
  // Any {{variables. occurrence that is not valid grammar is a configuration error.
  // classifyVariableExpression works on the whole result string; scan for each
  // {{variables. prefix individually so we can surface the exact expression.
  const malformedVarRe = /\{\{variables\.[^}]*\}?\}?/g;
  let mv: RegExpExecArray | null;
  malformedVarRe.lastIndex = 0;
  while ((mv = malformedVarRe.exec(result)) !== null) {
    const candidate = mv[0];
    if (classifyVariableExpression(candidate) === 'malformed') {
      throw new Error(
        `Malformed variable template "${candidate}": ` +
        `expected {{variables.<name>}} where <name> contains no dots. ` +
        `This is a configuration error — the action cannot execute.`,
      );
    }
  }

  // Replace variable placeholders — name must not contain dots (Step 18.1 grammar).
  // Using [^}.] mirrors the extractor's VARIABLE_RE so runtime and Inspector agree.
  result = result.replace(
    /\{\{variables\.([^}.]+)\}\}/g,
    (_match, name: string) => {
      if (!declaredVariableNames.has(name)) {
        throw new Error(
          `Template references variable "${name}" which is not declared in project.variables.`,
        );
      }
      return variableValueToString(variableState[name]);
    },
  );

  return result;
}

function interpolateRecord(
  record: Record<string, string>,
  componentsByName: Map<string, string>,
  componentState: Record<string, ComponentRuntimeState>,
  variableState: Record<string, unknown>,
  declaredVariableNames: Set<string>,
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, val] of Object.entries(record)) {
    result[key] = interpolateString(val, componentsByName, componentState, variableState, declaredVariableNames);
  }
  return result;
}

/**
 * Returns a rendered copy of the action with all templates resolved.
 * Throws if any referenced component or undeclared variable is found.
 * The original action is never mutated.
 *
 * Path-parameter values use the same component and variable interpolation as
 * other request fields. The backend then substitutes the rendered values into
 * bare URL placeholders such as {{itemId}}.
 */
export function renderAction(
  action: RestAction,
  componentsByName: Map<string, string>,
  componentState: Record<string, ComponentRuntimeState>,
  variableState: Record<string, unknown>,
  declaredVariableNames: Set<string>,
): RestAction {
  return {
    ...action,
    url: interpolateString(action.url, componentsByName, componentState, variableState, declaredVariableNames),
    headers: action.headers
      ? interpolateRecord(action.headers, componentsByName, componentState, variableState, declaredVariableNames)
      : action.headers,
    queryParameters: action.queryParameters
      ? interpolateRecord(action.queryParameters, componentsByName, componentState, variableState, declaredVariableNames)
      : action.queryParameters,
    pathParameters: action.pathParameters
      ? interpolateRecord(action.pathParameters, componentsByName, componentState, variableState, declaredVariableNames)
      : action.pathParameters,
    bodyTemplate: action.bodyTemplate
      ? interpolateString(action.bodyTemplate, componentsByName, componentState, variableState, declaredVariableNames)
      : action.bodyTemplate,
  };
}
